import { Helmet } from "react-helmet-async";
import { Search, MapPin } from "lucide-react";
import heroImage from "@/assets/skiathos2.jpg";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useState } from "react";


const HeroSection = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");

  const isGreek = i18n.language === "el";

  const titleScript = isGreek ? "Ανακαλύψτε τη Σκιάθο" : "Discover Skiathos";
  const titleMain = isGreek ? "Σαν Ντόπιοι" : "Like a Local";
  const subtitlePart1 = isGreek
    ? "Βρείτε εκδρομές με σκάφος, βίλες, μεταφορές, δραστηριότητες και τοπικές εμπειρίες"
    : "Find boat trips, villas, transfers, activities and local experiences";
  const subtitlePart2 = isGreek 
    ? "όλα σε ένα μέρος." 
    : "all in one place.";
  const placeholderText = isGreek ? "Τι ψάχνετε;" : "What are you looking for?";

  const chips = [
    { label: isGreek ? "Ημερήσιες Κρουαζιέρες" : "Daily Cruises", href: "/skiathos-daily-cruises" },
    { label: isGreek ? "Ενοικιάσεις Σκαφών" : "Boat Rentals", href: "/private-boat-rentals-in-skiathos" },
    { label: isGreek ? "Βίλες & Καταλύματα" : "Villas & Stays", href: "/accommodations" },
    { label: isGreek ? "Μεταφορές" : "Transfers", href: "/private-transfers" },
    { label: isGreek ? "Δραστηριότητες" : "Things to Do", href: "/activities" },
  ];

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  const handleChipClick = (href: string) => {
    navigate(href);
  };

  return (
    <section className="relative min-h-[100dvh] flex items-center justify-center pt-32 lg:pt-40 pb-20 overflow-hidden bg-ocean-deep">
      <Helmet>
        <link rel="preload" as="image" href={heroImage} fetchPriority="high" />
      </Helmet>

      <div className="absolute inset-0 bg-ocean-deep">
        <video
          autoPlay
          loop
          muted
          playsInline
          poster={heroImage}
          className="absolute inset-0 w-full h-full object-cover object-[center_30%] motion-reduce:hidden"
        >
          <source src="/videos/skiathos-hero.mp4" type="video/mp4" />
        </video>
        <img
          src={heroImage}
          alt="Skiathos coastline"
          className="hidden motion-reduce:block absolute inset-0 w-full h-full object-cover object-center"
        />

        {/* Cinematic overlays matching cruise details */}
        <div className="absolute inset-0 bg-gradient-to-b from-ocean-deep/60 via-transparent to-ocean-deep/90" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_transparent_30%,_rgba(0,0,0,0.65)_100%)]" />
      </div>

      <div className="container relative z-10 mx-auto px-4 text-center flex flex-col items-center max-w-5xl">
        <h1 className="font-display mb-5 md:mb-8 flex flex-col items-center leading-[1.1] animate-fade-in-up text-center drop-shadow-[0_4px_24px_rgba(0,0,0,0.8)]">
          <span className="text-4xl md:text-6xl lg:text-7xl font-extrabold text-white tracking-tight">
            {titleScript}
          </span>
          <span className="mt-2 md:mt-3 text-5xl md:text-7xl lg:text-[84px] font-script text-secondary drop-shadow-[0_4px_16px_rgba(0,0,0,0.8)] -rotate-2">
            {titleMain}
          </span>
        </h1>

        <p
          className="text-lg sm:text-xl lg:text-2xl text-white mb-8 max-w-3xl mx-auto font-body font-semibold drop-shadow-[0_4px_16px_rgba(0,0,0,0.9)] animate-fade-in-up tracking-wide leading-relaxed"
          style={{ animationDelay: "0.1s" }}
        >
          {subtitlePart1} <br />
          <span className="text-secondary font-bold">— {subtitlePart2}</span>
        </p>

        <div className="w-full max-w-2xl mx-auto mb-6 animate-fade-in-up" style={{ animationDelay: "0.1s" }}>
          <form
            onSubmit={handleSearchSubmit}
            className="relative flex items-center bg-black/20 backdrop-blur-md border border-white/20 rounded-full p-1.5 shadow-[0_8px_32px_rgba(0,0,0,0.5)] focus-within:ring-2 focus-within:ring-white/50 transition-all duration-300"
          >
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={placeholderText}
              className="w-full pl-6 pr-14 py-3 sm:py-3.5 bg-transparent text-white placeholder:text-white/85 text-sm sm:text-base font-body font-medium focus:outline-none rounded-full"
            />
            <button
              type="submit"
              aria-label={isGreek ? "Αναζήτηση" : "Search"}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-secondary text-secondary-foreground flex items-center justify-center transition-all hover:scale-[1.05] active:scale-95 duration-200 shadow-[0_0_15px_rgba(var(--secondary),0.5)]"
            >
              <Search className="h-4 w-4 sm:h-5 sm:w-5" strokeWidth={2.5} />
            </button>
          </form>
        </div>

        <div
          className="flex flex-wrap items-center justify-center gap-2.5 sm:gap-3 max-w-3xl mx-auto mb-8 animate-fade-in-up"
          style={{ animationDelay: "0.2s" }}
        >
          {chips.map((chip) => (
            <button
              key={chip.href}
              onClick={() => handleChipClick(chip.href)}
              className="px-4 py-2 sm:px-5 sm:py-2.5 rounded-full border border-white/20 text-white bg-black/30 backdrop-blur-md hover:bg-black/40 hover:border-white/40 hover:shadow-[0_0_20px_rgba(255,255,255,0.15)] transition-all text-[11px] sm:text-[12px] font-bold tracking-wider uppercase font-body duration-300"
            >
              {chip.label}
            </button>
          ))}
        </div>

      </div>
    </section>
  );
};

export default HeroSection;
